import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { landAxios } from '@Root/utils/landAxios'
import MyHouseSubscribedInsurance from '@Types/entities/MyHouseSubscribedInsurance'
import MyHouseInsuranceResponse from '@Types/entities/MyHouseInsuranceResponse'
import { FETCH_STATUS } from '@Root/contants'
import InsuranceSelectBoxResponse from '@Types/entities/InsuranceSelectBoxResponse'
import InsuranceHouseResponse from '@Types/entities/InsuranceHouseResponse'
import { InsuranceParam } from '@Root/slices/myhouse/insuranceRegister'

export type InsuranceSelectBox = {
  fetchStatus: FETCH_STATUS
} & InsuranceSelectBoxResponse

interface InsuranceExpand {
  expandText: string
  itemCount: number
}

export type InsuranceHouse = {
  fetchStatus: FETCH_STATUS
} & InsuranceHouseResponse

export type SubscribedInsurance = {
  fetchStatus: FETCH_STATUS
} & MyHouseSubscribedInsurance

interface InsuranceState {
  hasMore: boolean
  totalCount: number
  isExpanded: boolean
  insuranceExpand: InsuranceExpand
  subscribedList: MyHouseSubscribedInsurance[]
  subscribedInsurance: SubscribedInsurance
  insuranceSelectBox: InsuranceSelectBox
  insuranceHouse: InsuranceHouse
}

const initialState: InsuranceState = {
  hasMore: false,
  totalCount: 0,
  isExpanded: false,
  insuranceExpand: {
    expandText: '더보기',
    itemCount: 2,
  },
  subscribedList: [],
  subscribedInsurance: {
    fetchStatus: FETCH_STATUS.INITIAL,
  } as SubscribedInsurance,
  insuranceHouse: {
    fetchStatus: FETCH_STATUS.INITIAL,
  } as InsuranceHouse,
  insuranceSelectBox: {
    insuranceCompanies: {},
    insuranceProducts: {},
    fetchStatus: FETCH_STATUS.INITIAL,
  } as InsuranceSelectBox,
} as InsuranceState

export const fetchInsuranceList = createAsyncThunk('myhouse/fetchInsuranceList', async (houseNo: number) => await (await landAxios.get(`myhouse/${houseNo}/insurances`)).data)

export const fetchInsurance = createAsyncThunk(
  'myhouse/fetchInsurance',
  async (param: InsuranceParam) => await (await landAxios.get(`myhouse/${param.houseNo}/insurance/${param.insuranceSequence}`)).data,
)

export const fetchInsuranceHouse = createAsyncThunk(
  'myhouse/fetchInsurance/house',
  async (houseNo: number) => await (await landAxios.get(`myhouse/${houseNo}/insurance/house`)).data,
)

export const fetchInsuranceSelectBox = createAsyncThunk('myhouse/fetchInsuranceSelectBox', async () => {
  return await (await landAxios.get('myhouse/insurance/selectbox')).data
})

const myhouseInsuranceSlice = createSlice({
  name: 'myhouseInsurance',
  initialState,
  reducers: {
    initializeInsurance: (state: InsuranceState) => {
      Object.assign(state, initialState)
    },
    updateExpanded: (state: InsuranceState, action: PayloadAction<boolean>) => {
      state.isExpanded = action.payload
      if (state.isExpanded) {
        state.insuranceExpand = {
          expandText: '접기',
          itemCount: state.totalCount,
        }
      } else {
        state.insuranceExpand = {
          expandText: '더보기',
          itemCount: 2,
        }
      }
    },
  },
  extraReducers: {
    [fetchInsuranceList.fulfilled.type]: (state: InsuranceState, action: PayloadAction<MyHouseInsuranceResponse>) => {
      state.hasMore = action.payload.hasMore
      state.totalCount = action.payload.totalCount
      state.subscribedList = action.payload.subscribedInsurances
    },
    [fetchInsurance.fulfilled.type]: (state: InsuranceState, action: PayloadAction<MyHouseSubscribedInsurance>) => {
      state.subscribedInsurance = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
    [fetchInsuranceHouse.fulfilled.type]: (state: InsuranceState, action: PayloadAction<InsuranceHouseResponse>) => {
      state.insuranceHouse = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
    [fetchInsuranceSelectBox.fulfilled.type]: (state: InsuranceState, action: PayloadAction<InsuranceSelectBoxResponse>) => {
      state.insuranceSelectBox = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
  },
})

export const { initializeInsurance, updateExpanded } = myhouseInsuranceSlice.actions
export default myhouseInsuranceSlice.reducer
